import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../../api/client';
import { apiErrorText } from '../../api/errorText';
import { useApi } from '../../api/useApi';
import { useDiscoveryState } from '../../api/useDiscoveryState';
import AsyncSection from '../../components/AsyncSection';
import ManagedSetting from './ManagedSetting';

/// The server's own identity - what it calls itself, and where clients reach it - and what its
/// library is: what it holds, or everything that exists.
export default function GeneralSettings() {
  const { t } = useTranslation();
  const settings = useApi(['settings'], api.settings);
  const queryClient = useQueryClient();

  // A draft, or the value the server reports. No effect keeps the two in step, so they cannot
  // fall out of step either.
  const [draftServerName, setDraftServerName] = useState<string>();
  const [draftPublicUrl, setDraftPublicUrl] = useState<string>();
  const [draftLocalNetworks, setDraftLocalNetworks] = useState<string>();
  const [draftCatalogue, setDraftCatalogue] = useState<string>();
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const serverName = draftServerName ?? settings.data?.serverName.value ?? '';
  const publicUrl = draftPublicUrl ?? settings.data?.publicUrl.value ?? '';
  const localNetworks = draftLocalNetworks ?? settings.data?.localNetworks.value ?? '';
  const catalogue = draftCatalogue ?? (settings.data?.catalogueInLibrary.value ? 'true' : 'false');

  // The whole catalogue can only stand in for the library where something can deliver any title
  // in it. Without such a source the switch cannot be turned on - but it can always be turned
  // off, so a setting left on from before is never stuck there. A server that does not say
  // either way is left to answer the save itself.
  const discovery = useDiscoveryState();
  const catalogueUnavailable = discovery.catalogueInLibraryAvailable === false
    && settings.data?.catalogueInLibrary.value !== true;

  const save = async () => {
    setBusy(true);
    setSaved(false);
    setError(undefined);

    try {
      await api.saveSettings({
        serverName,
        publicUrl,
        localNetworks,
        catalogueInLibrary: catalogue === 'true',
      });

      setDraftServerName(undefined);
      setDraftPublicUrl(undefined);
      setDraftLocalNetworks(undefined);
      setDraftCatalogue(undefined);
      settings.reload();

      // The navigation and the library screens draw from this; they should not wait for it to go
      // stale to show what was just switched.
      void queryClient.invalidateQueries({ queryKey: ['discovery', 'state'] });
      setSaved(true);
    } catch (caught) {
      setError(apiErrorText(caught, t).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AsyncSection loading={settings.loading} error={settings.error} onRetry={settings.reload}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={3}>
            {error && <Alert severity="error">{error}</Alert>}
            {saved && <Alert severity="success">{t('settings.saved')}</Alert>}

            <ManagedSetting
              setting={settings.data?.serverName}
              label={t('settings.general.serverName')}
              hint={t('settings.general.serverNameHint')}
              value={serverName}
              onChange={setDraftServerName}
            />

            <ManagedSetting
              setting={settings.data?.publicUrl}
              label={t('settings.general.publicUrl')}
              hint={t('settings.general.publicUrlHint')}
              value={publicUrl}
              onChange={setDraftPublicUrl}
              placeholder="http://emiro.local:1810"
            />

            <ManagedSetting
              setting={settings.data?.localNetworks}
              label={t('settings.general.localNetworks')}
              hint={t('settings.general.localNetworksHint')}
              value={localNetworks}
              onChange={setDraftLocalNetworks}
              placeholder="192.168.0.0/16, 10.0.0.0/8"
            />

            <ManagedSetting
              setting={settings.data?.catalogueInLibrary}
              label={t('settings.general.catalogue')}
              hint={catalogueUnavailable ? t('settings.general.catalogueUnavailable') : t('settings.general.catalogueHint')}
              value={catalogue}
              onChange={setDraftCatalogue}
              disabled={catalogueUnavailable}
              choices={[
                { value: 'false', label: t('settings.general.catalogueOff') },
                { value: 'true', label: t('settings.general.catalogueOn') },
              ]}
            />

            <Box>
              <Button variant="contained" onClick={() => void save()} disabled={busy}>
                {t('common.save')}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </AsyncSection>
  );
}
